<template>
  <material-card class="mx-2">
    <div slot="header" class="mx-3 py-2">
      <h3 v-if="!benefit.shared">{{ benefit.partner.name }} {{ benefit.place ? '- ' + benefit.place.name : '' }}</h3>

      <h3>{{ benefit.name }}</h3>
    </div>

    <v-layout>
      <v-flex xs6>
        <div class="mr-2">Rodzaj: {{ benefit.typeTranslation }}</div>

        <div class="mr-2">{{ benefit.displayLabel }}: {{ benefit.displayValue }}</div>

        <div v-if="benefit.max" class="mr-2">Maksymalna liczba użyć: {{ benefit.max }}</div>

        <div v-if="benefit.maxPerUser" class="mr-2">
          Maksymalna liczba użyć jednego użytkownika: {{ benefit.maxPerUser }}
        </div>

        <div v-if="benefit.waitTime" class="mr-2">Okres karencji w sekundach: {{ benefit.waitTime }}</div>
      </v-flex>

      <v-flex xs6>
        <div>
          <div v-if="benefit.place" class="mr-2">Obiekt: {{ benefit.place.name }}</div>

          <div v-if="benefit.displayGroups" class="mr-2">
            Grupy:
            {{ benefit.displayGroups }}
          </div>

          <div v-if="benefit.displayCategories" class="mr-2">
            Kategorie:
            {{ benefit.displayCategories }}
          </div>

          <div v-if="benefit.shared" class="mr-2">
            Partnerzy:
            <ul v-if="benefit.partners.length">
              <li v-for="(partner, index) in benefit.partners" :key="'partner_' + index">
                <router-link :to="{ name: 'partner', params: { id: partner.id } }">{{ partner.name }}</router-link>
              </li>
            </ul>

            <div v-else>Brak przypisanych partnerów</div>
          </div>

          <span v-if="benefit.start || benefit.end">
            <span v-if="benefit.start && benefit.end">Ważna od {{ benefit.start }} do {{ benefit.end }}</span>

            <span v-else-if="benefit.start">Ważna od {{ benefit.start }}</span>

            <span v-else-if="benefit.end">Ważna do {{ benefit.end }}</span>
          </span>
        </div>
      </v-flex>
    </v-layout>

    <div v-if="benefit.descriptionShort" class="mt-2">
      <h4>Skrócony opis</h4>

      <p>{{ benefit.descriptionShort }}</p>
    </div>

    <div v-if="benefit.description" class="mt-2">
      <h4>Opis</h4>

      <div v-html="benefit.description" />
    </div>

    <v-layout align-start justify-space-between mx-2 row slot="actions">
      <span>
        <v-btn @click="$router.go(-1)">Wróć</v-btn>

        <v-btn v-if="!benefit.shared" :to="{ name: 'partner', params: { id: benefit.partner.id } }">
          Profil Partnera
        </v-btn>
      </span>

      <span>
        <span v-if="isNotConfirmed">
          <v-btn dark color="red darken-2" @click="showDeleteDialog = true">
            <v-icon left>mdi-close</v-icon>Odrzuć
          </v-btn>

          <v-btn dark color="blue darken-2" @click="showConfirmDialog = true">
            <v-icon left>mdi-check</v-icon>Zaakceptuj
          </v-btn>
        </span>

        <span v-if="benefit.shared">
          <check-permission permissions="SHARED_BENEFIT.ASSIGN">
            <v-btn dark color="blue darken-2" @click="showAssigmentModal">
              <v-icon left>mdi-folder-account</v-icon>Przypisz parnterów
            </v-btn>
          </check-permission>

          <check-permission permissions="SHARED_BENEFIT.UPDATE">
            <v-btn dark color="blue darken-2" @click="showEditModal"> <v-icon left>mdi-pencil</v-icon> Edycja </v-btn>
          </check-permission>
        </span>
      </span>
    </v-layout>

    <material-dialog
      title="Potwierdź usunięcie"
      text="Czy na pewno chcesz usunąć usługę?"
      :show="showDeleteDialog"
      @close="closeDialog"
      @confirm="remove"
    />

    <material-dialog
      cancelColor="error"
      confirmColor="green darken-1"
      title="Potwierdź aktywacje"
      text="Czy na pewno chcesz aktywować usługę?"
      :show="showConfirmDialog"
      @close="closeDialog"
      @confirm="confirm"
    />

    <assigment-modal
      v-if="benefit.shared"
      type="partner"
      :id="benefit.id"
      :current="benefit.partners"
      :show="assigmentModal"
      @close="assigmentModal = false"
    />

    <benefit-modal
      :show="editModal"
      :id="benefit.id"
      :edit="true"
      :shared="benefit.shared"
      @close="hideEditModal"
      @update="update"
    />
  </material-card>
</template>

<script>
import {
  BenefitIconTypeTranslation,
  BenefitType,
  benefitType,
  BenefitTypeLabel,
  BenefitTypeTranslation,
  DisplayBenefitType,
  PublicSettings,
  StateString
} from '@witkac/kartamiejska-shared';
import { mapState } from 'vuex';

import MaterialCard from '@/components/material/Card';
import MaterialDialog from '@/components/material/Dialog';
import AssigmentModal from '@/components/modals/Assigment.modal';
import { notificationsMixin } from '@/mixins/notifications';
import BenefitModal from '@/views/partners/tabs/modals/AddBenefit.modal';

// @vuese
// Szczegóły usługi
// @group Benefit
export default {
  components: { MaterialCard, MaterialDialog, AssigmentModal, BenefitModal },
  data() {
    return {
      showDeleteDialog: false,
      showConfirmDialog: false,
      assigmentModal: false,
      editModal: false
    };
  },
  mixins: [notificationsMixin],
  methods: {
    // @vuese
    // Metoda dostosowuje usługę do prawidłowego wyświetlenia informacji
    setDisplayData() {
      this.benefit.displayType = BenefitIconTypeTranslation[BenefitType[this.benefit.type]];
      if (this.benefit.type === DisplayBenefitType.PERCENT_DISCOUNT) {
        this.benefit.displayValue = this.benefit.value + ' %';
      } else {
        this.benefit.displayValue = this.benefit.value;
      }
      if (this.benefit.groups.length) {
        this.benefit.displayGroups = this.benefit.groups.map(group => group.name).join(', ');
      } else {
        this.benefit.displayGroups = this.noGroupSetting;
      }
      if (this.benefit.categories) {
        this.benefit.displayCategories = this.benefit.categories.map(category => category.name).join(', ');
      }
      if (this.benefit.type) {
        this.benefit.typeTranslation = BenefitTypeTranslation[BenefitType[this.benefit.type]];
        const displayLabel = BenefitTypeLabel[this.benefit.type];
        this.benefit.displayLabel = displayLabel.endsWith('*')
          ? displayLabel.substring(0, displayLabel.length - 1)
          : displayLabel;
      }
    },
    // @vuese
    // Usuwa usługi
    remove() {
      this.$store
        .dispatch('benefit/remove', this.benefit.id)
        .then(() => this.$router.push({ name: 'benefits' }))
        .catch(() => this.$store.dispatch('notifyError', { title: 'Nie udało się odrzucić usługi' }))
        .finally(() => {
          this.showDeleteDialog = false;
        });
    },
    // @vuese
    // Potwierdzenie usługi
    confirm() {
      this.$store
        .dispatch('benefit/confirm', this.benefit.id)
        .then(() => this.$router.push({ name: 'benefits' }))
        .catch(error => this.$store.dispatch('notifyError', { title: 'Nie udało się zaakceptować usługi' }))
        .finally(() => {
          this.showConfirmDialog = false;
        });
    },
    // @vuese
    // Zamyka dialog
    closeDialog() {
      this.showDeleteDialog = false;
      this.showConfirmDialog = false;
    },
    // @vuese
    // Pokazanie modala przypisania partnerów
    showAssigmentModal() {
      this.assigmentModal = true;
    },
    // @vuese
    // Edycja partnera
    update(data) {
      return this.$store
        .dispatch(`benefit/update`, data)
        .then(() => {
          this.notifySuccess('Zapisano zmiany w usłudze');
          this.hideEditModal();
        })
        .catch(err => this.notifyError('Nie udało się zapisać danych usługi'));
    },
    // @vuese
    // Ukrycie modala edycji usługi
    hideEditModal() {
      this.editModal = false;
    },
    // @vuese
    // Pokazanie modala edycji usługi
    showEditModal() {
      this.editModal = true;
    }
  },
  computed: {
    ...mapState({
      benefit: state => state.benefit.data,
      noGroupSetting: state => state.settings.settings[PublicSettings.NO_GROUP_SELECTED_NAME].value
    }),
    isNotConfirmed() {
      return this.benefit.state === StateString.NOT_CONFIRMED;
    }
  },
  watch: {
    benefit: {
      handler() {
        this.setDisplayData();
      },
      immediate: true
    }
  }
};
</script>
